import s from './PurchasedItem.module.css'
import Image from "next/legacy/image"
import { Button } from '@components/ui'
import isEmpty from 'lodash.isempty'
import useCustomerKitsSearch from '@lib/hooks/useCustomerKitsSearch'
import { Types } from 'mongoose'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { getResultStatus, getSingleFieldFromObjectArray, showHtml } from '@lib/utils/utils'
import { useCustomerOrderKitsResult } from '@lib/hooks/useCustomerKit'
import { useRouter } from 'next/router'
import moment from 'moment'

export interface PurchasedItemProps {
  line1?: string | React.ReactNode
  line2?: string | React.ReactNode
  line3?: string | React.ReactNode
  ProductName?: string
  PurchaseDate?: string
  image?: string
  link?: any
  buttonHandler?: () => void;
  buttonCta?: string
  hasButton?: boolean
  buttonClass?: string
  id?: string
  kitId?: string
  state?: any
  howTo?: boolean
  howToText?: string
  howToTextLink?: string
  kit?: any
  status?: any
  isVisible?: boolean
  priority?: boolean
  alreadyActivated?: boolean
}

const PurchasedItem: React.FC<PurchasedItemProps> = ((props) => {
  const {
    image,
    link,
    line1,
    line2,
    line3,
    buttonHandler,
    buttonCta,
    hasButton,
    buttonClass,
    id,
    kitId,
    state,
    howTo,
    howToText,
    howToTextLink,
    status,
    kit,
    isVisible = true,
    priority = false,
    alreadyActivated = false
  }: any = props

  const t = useTranslations('Account')
  const router = useRouter()
  const customerOrderKitsResult = useCustomerOrderKitsResult
  const customerKitsSearch = useCustomerKitsSearch
  const [subKitResults, setSubKitResults] = useState<any>(null)

  let subKits:any = null
  let upsellKits:any = null

  if(kit?.upsellKit){
    upsellKits = [kit?.upsellKit]
  }

  if (kit?.subKit) {
    subKits = [kit?.subKit]
  }

  // Fetch subKit results when subKit exists
  useEffect(() => {
    if (!kit?.subKit) {
      setSubKitResults(null)
      return
    }

    const subKitsArray = [kit.subKit]
    const labOrderIds: any = getSingleFieldFromObjectArray(subKitsArray, 'lab_order_id')
    if (!labOrderIds || labOrderIds.length === 0) {
      setSubKitResults({})
      return
    }

    customerOrderKitsResult(labOrderIds)
      .then(response => response.json())
      .then(value => {
        let _results: any = {}
        
        // Handle case where result is null (no results yet)
        if (value?.result !== undefined && value?.result.length > 0 && value?.result[0] === null) {
          setSubKitResults({})
          return
        }

        // Process order statuses if they exist
        if (value?.result?.orderStatuses && Array.isArray(value.result.orderStatuses)) {
          value.result.orderStatuses.forEach((res: { statusId: number; status: string; orderID: any }) => {
            res.statusId = getResultStatus(res.status)
            res.status = 'status' + getResultStatus(res.status)
            _results[res.orderID] = res
          })

          // Update state with results
          setSubKitResults(_results)
        } else {
          setSubKitResults({})
        }
      })
      .catch(error => {
        console.error('Error fetching subKit results:', error)
        setSubKitResults({})
      })
  }, [kit?.subKit, customerOrderKitsResult])

  return isVisible && (
    <li className={s.root}>
      <div className={'relative'}>
        <div
          className={`w-full aspect-h-6 aspect-w-12 overflow-hidden relative rounded-cornerS bg-transparent relative`}>
          {image && <Image alt='Product image' src={image} layout='fill' objectFit='cover' priority={priority} />}
        </div>
        <div
          className={'bg-' + state?.bgClass + ' text-' + state?.colorClass + ' absolute text-paragraph03 font-medium top-space2 right-space2 rounded-cornerS px-space6 py-space2'}>{state?.text}</div>
        <div className={s.textCover}>
          <p className={`${s.textLine1} text-paragraph02 font-medium mt-space6`}
             dangerouslySetInnerHTML={showHtml(line1 ?? '')}>
          </p>
          <p className={`${s.textLine2} text-paragraph03 text-neutral600 mt-space1`}
             dangerouslySetInnerHTML={showHtml(line2 ?? '')}>
          </p>
          <p className={'text-paragraph03 text-neutral600 mt-space1 mb-space1'} dangerouslySetInnerHTML={showHtml(status ?? '')}>
          </p>
          <p className={'text-paragraph03 text-neutral600 absolute'}>
            {(upsellKits && upsellKits[0]?.shopify_order_name ? upsellKits[0].shopify_order_name + ' - ' + upsellKits[0]?.shopify_line_item.name : '')}
          </p>
        </div>
        <div className={'min-h-e61 mt-space8'}>
          {hasButton && <Button href={link}
                                className={buttonClass + ' float-right my-space2'}
                                style={buttonClass?.includes('disabled') ? { opacity: 0.6 } : {}}
                                variant='outlineSmall'
                                onClick={buttonHandler}>{buttonCta}</Button>}
          {howTo && <a href={howToTextLink}
                       className={'float-right text-button text-neutral900 hover:text-neutral700 transition-all ease-in-out mt-space7 underline mr-space6 font-medium'}>{howToText}</a>}
          {alreadyActivated && <span className={'float-right text-paragraph03 text-tertiary500 font-medium mt-space7 mr-space6'}>{t('alreadyActivated')}</span>}
        </div>
        <div className={'mt-auto'}>
          {line3 && (
            <div className={'mt-space4 pt-space4 border-t border-neutral400'}>
              <span
                className={'inline-block bg-tertiary50 text-tertiary500 text-paragraph03 font-medium rounded-cornerXS px-space4 py-space1'}
                style={{ fontFamily: "var(--fontMono)", letterSpacing: '0.5px' }}
                dangerouslySetInnerHTML={showHtml(line3 ?? '')}
              />
            </div>
          )}
        </div>
      </div>
      {subKits && <div>
        {subKits.map((subKit: any, index:number) => {
          return (
          <div key={'subKit' + index} className={'border-t-2 border-dashed mt-space8 py-space4 pb-space20'}>
            <p className={'font-medium block pb-space1'}>{t('forwardingKit') + ' ' + subKit?.shopify_order_name}</p>
            <p className={'block text-paragraph03 text-neutral600 pb-space1'}>{t('forwardingKitInfo')}</p>
            <p
              className={'block text-paragraph03 text-neutral600 pb-space8'}>{t('forwardingKitDate') + ' ' + moment(subKit?.createdAt).format('DD.MM.YYYY')}</p>
            {(() => {
              // Calculate shared values for button text and navigation
              const hasLabOrderId = !!subKit?.lab_order_id
              const hasResults = hasLabOrderId && subKitResults && subKitResults[subKit.lab_order_id]
              const result = hasResults ? subKitResults[subKit.lab_order_id] : null
              const isCompletedStatus = result && (result.statusId === 5 || result.statusId === 7)

              // Determine button text based on lab_order_id, results, and status
              let buttonText: string
              if (!hasLabOrderId) {
                buttonText = t('activateKit')
              } else if (hasResults && ((subKit.is_pickup && isCompletedStatus) || !subKit.is_pickup)) {
                // Show status if: (pickup with completed status) OR (not pickup with any results)
                buttonText = t(result.status)
              } else if (subKit.is_pickup) {
                buttonText = t('pickUpWaiting')
              } else {
                buttonText = t('labWaitingForKit')
              }

              return (
                <Button className={buttonClass + ' float-right my-space2'}
                        variant='outlineSmall'
                        onClick={() => {
                          // Determine pathname based on lab_order_id, results, and pickup status
                          let pathname: string
                          if (hasLabOrderId) {
                            // If pickup and not completed, go to activateKits; otherwise go to orders/kits
                            if (subKit.is_pickup && !isCompletedStatus) {
                              pathname = `/activateKits/${subKit._id}`
                            } else {
                              pathname = `/orders/kits/${subKit._id}`
                            }
                          } else {
                            pathname = `/activateKits/${subKit?._id}`
                          }

                          // Add pickup query parameter only for pickup items with lab_order_id
                          const query = hasLabOrderId && subKit?.is_pickup ? { pickup: 1 } : {}

                          router.push({ pathname, query })
                        }}>{buttonText}</Button>
              )
            })()}
          </div>
        )})}
      </div>
      }
    </li>
  )
})

export default PurchasedItem
